import React, { useState, useEffect, useCallback } from 'react';
import { ManagedUserDetail, AccountStatus } from '../types';
import { UserManagementService } from '../services/UserManagementService';
import './UserDetail.css';

interface UserDetailProps {
  userId: string;
  onClose: () => void;
  onStatusChanged: (userId: string, newStatus: AccountStatus) => void;
}

export const UserDetail: React.FC<UserDetailProps> = ({ userId, onClose, onStatusChanged }) => {
  const [user, setUser] = useState<ManagedUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<AccountStatus | null>(null);
  const [statusReason, setStatusReason] = useState('');
  const [updating, setUpdating] = useState(false);

  const service = UserManagementService.getInstance();

  useEffect(() => {
    loadUserDetail();
  }, [userId]);

  const loadUserDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await service.getUserDetail(userId);
      setUser(result.user);
    } catch (err: any) {
      if (err.message === 'USER_NOT_FOUND') {
        setError('User not found');
      } else {
        setError('Failed to load user details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusClick = useCallback((status: AccountStatus) => {
    setPendingStatus(status);
    setStatusReason('');
    setShowConfirmDialog(true);
  }, []);

  const handleConfirmStatusChange = async () => {
    if (!pendingStatus || !user) return;

    setUpdating(true);
    const previousStatus = user.status;

    // Optimistic update
    setUser({ ...user, status: pendingStatus });
    setShowConfirmDialog(false);

    try {
      await service.updateUserStatus(userId, pendingStatus, statusReason || undefined);
      onStatusChanged(userId, pendingStatus);
    } catch (err) {
      // Revert optimistic update
      setUser({ ...user, status: previousStatus });
      setError('Failed to update status. Please try again.');
    } finally {
      setUpdating(false);
      setPendingStatus(null);
    }
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="user-detail user-detail--loading">
        <div className="user-detail__header">
          <button className="user-detail__close-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="user-detail__loading">Loading user details...</div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="user-detail user-detail--error">
        <div className="user-detail__header">
          <button className="user-detail__close-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="user-detail__error">{error}</div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="user-detail">
      <div className="user-detail__header">
        <h3 className="user-detail__title">User Details</h3>
        <button className="user-detail__close-btn" onClick={onClose} aria-label="Close">✕</button>
      </div>

      {error && <div className="user-detail__toast user-detail__toast--error">{error}</div>}

      {/* Profile Section */}
      <section className="user-detail__section">
        <h4 className="user-detail__section-title">Profile</h4>
        <div className="user-detail__grid">
          <div className="user-detail__field">
            <span className="user-detail__label">Name</span>
            <span className="user-detail__value">{user.name}</span>
          </div>
          <div className="user-detail__field">
            <span className="user-detail__label">Email</span>
            <span className="user-detail__value">{user.email || 'N/A'}</span>
          </div>
          <div className="user-detail__field">
            <span className="user-detail__label">User ID</span>
            <span className="user-detail__value user-detail__value--mono">{user.id}</span>
          </div>
          <div className="user-detail__field">
            <span className="user-detail__label">Registration Method</span>
            <span className="user-detail__value">{user.authMethod}</span>
          </div>
          <div className="user-detail__field">
            <span className="user-detail__label">Date Registered</span>
            <span className="user-detail__value">{formatDate(user.createdAt)}</span>
          </div>
          <div className="user-detail__field">
            <span className="user-detail__label">Last Active</span>
            <span className="user-detail__value">{formatDate(user.lastActiveAt)}</span>
          </div>
          <div className="user-detail__field">
            <span className="user-detail__label">Account Status</span>
            <span className={`user-detail__status-badge user-detail__status-badge--${user.status}`}>
              {user.status}
            </span>
          </div>
        </div>
      </section>

      {/* Gaming Statistics Section */}
      <section className="user-detail__section">
        <h4 className="user-detail__section-title">Gaming Statistics</h4>
        <div className="user-detail__stats-grid">
          <div className="user-detail__stat">
            <span className="user-detail__stat-value">{user.gamesPlayed}</span>
            <span className="user-detail__stat-label">Games Played</span>
          </div>
          <div className="user-detail__stat">
            <span className="user-detail__stat-value">{Math.round(user.winRate * 100)}%</span>
            <span className="user-detail__stat-label">Win Rate</span>
          </div>
          <div className="user-detail__stat">
            <span className="user-detail__stat-value">{user.favoriteGame}</span>
            <span className="user-detail__stat-label">Favorite Game</span>
          </div>
          <div className="user-detail__stat">
            <span className="user-detail__stat-value">{formatDuration(user.totalTimePlayed)}</span>
            <span className="user-detail__stat-label">Total Time Played</span>
          </div>
          <div className="user-detail__stat">
            <span className="user-detail__stat-value">{Math.round(user.multiplayerRatio * 100)}%</span>
            <span className="user-detail__stat-label">Multiplayer Ratio</span>
          </div>
        </div>
      </section>

      {/* Game Breakdown */}
      {user.gameBreakdown && user.gameBreakdown.length > 0 && (
        <section className="user-detail__section">
          <h4 className="user-detail__section-title">Game Breakdown</h4>
          <div className="user-detail__breakdown">
            {user.gameBreakdown.map((entry) => (
              <div key={entry.game} className="user-detail__breakdown-row">
                <span className="user-detail__breakdown-game">{entry.game}</span>
                <span className="user-detail__breakdown-stats">
                  {entry.played} played, {entry.won} won
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Device & Region (conditional) */}
      {(user.deviceInfo || user.region) && (
        <section className="user-detail__section">
          <h4 className="user-detail__section-title">Device & Location</h4>
          <div className="user-detail__grid">
            {user.deviceInfo && (
              <div className="user-detail__field">
                <span className="user-detail__label">Device</span>
                <span className="user-detail__value">{user.deviceInfo}</span>
              </div>
            )}
            {user.region && (
              <div className="user-detail__field">
                <span className="user-detail__label">Region</span>
                <span className="user-detail__value">{user.region}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Account Status Controls */}
      <section className="user-detail__section">
        <h4 className="user-detail__section-title">Account Actions</h4>
        <div className="user-detail__actions">
          {user.status !== 'active' && (
            <button
              className="user-detail__action-btn user-detail__action-btn--activate"
              onClick={() => handleStatusClick('active')}
              disabled={updating}
            >
              Activate
            </button>
          )}
          {user.status !== 'suspended' && (
            <button
              className="user-detail__action-btn user-detail__action-btn--suspend"
              onClick={() => handleStatusClick('suspended')}
              disabled={updating}
            >
              Suspend
            </button>
          )}
          {user.status !== 'banned' && (
            <button
              className="user-detail__action-btn user-detail__action-btn--ban"
              onClick={() => handleStatusClick('banned')}
              disabled={updating}
            >
              Ban
            </button>
          )}
        </div>
      </section>

      {/* Confirmation Dialog */}
      {showConfirmDialog && pendingStatus && (
        <div className="user-detail__confirm-overlay">
          <div className="user-detail__confirm-dialog">
            <h4>Confirm Status Change</h4>
            <p>
              Change <strong>{user.name}</strong>'s status to{' '}
              <strong className={`user-detail__status-badge--${pendingStatus}`}>{pendingStatus}</strong>?
            </p>
            <textarea
              className="user-detail__reason-input"
              placeholder="Reason (optional)"
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
            />
            <div className="user-detail__confirm-actions">
              <button
                className="user-detail__confirm-btn user-detail__confirm-btn--cancel"
                onClick={() => setShowConfirmDialog(false)}
              >
                Cancel
              </button>
              <button
                className="user-detail__confirm-btn user-detail__confirm-btn--confirm"
                onClick={handleConfirmStatusChange}
                disabled={updating}
              >
                {updating ? 'Updating...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
